import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { AttachmentRenameComponent } from '@src/suite/common/components/attachments-list/modal/rename/attachment-rename';
import { CreateNewTemplateComponent } from '@src/suite/common/components/create-new-template/create-new-template';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { ViewStateOrder } from '@src/suite/common/data-models/complex-types/view-state-order';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { SingleViewStateRecord } from '@src/suite/common/data-models/records/single-viewstate-record';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { Convertor } from '@src/suite/common/services/convertor';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { FullscreenActions, OpenFullscreenModalActions, selectCurrentRecord, selectFullscreenNewRecord } from '@src/suite/common/store/fullscreen';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectListNewGuids } from '@src/suite/common/store/list/list.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { NavigationActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { selectSystemSingleViewStateItem } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentGroupBy, selectCurrentSortBy, selectCurrentViewState, selectCurrentViewStateType } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { selectGroupedSortedTopics } from '@src/suite/common/store/playbook/playbook.selector';
import { TIME_FIELD_SUFFIX } from '@src/suite/common/utils/global-tables.utils';
import { getTopicFieldsToUpdate } from '@src/suite/common/utils/record-utils';
import { Util } from '@src/suite/common/utils/util';
import { getDatabasesOptions } from '@src/suite/common/utils/viewstate-utils';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { PlaybookOpenAiService } from '@src/suite/playbook/services/playbook-open-ai-service';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { catchError, exhaustMap, filter, forkJoin, from, map, mergeMap, of, switchMap, take, throwError } from 'rxjs';

import {
    AssignmentRecordActions,
    DiscoverRecordActions,
    PublicationRecordActions,
    RecordHttpActions,
    RundownRecordActions,
    StoriesRecordActions,
    TopicRecordActions,
    UserRecordActions,
} from './records.actions';
import { selectAssignments, selectStoryRecordsMap, selectStoryTemplatesMap, selectTopicRecordByGuid, selectTopicRecordsMap, selectTopicTemplatesMap } from './records.selectors';

@Injectable()
export class RecordEffects {
    private playbookService = inject(PlaybookService);
    private playbookSearchService = inject(PlaybookSearchService);
    private rundownService = inject(RundownService);
    private appService = inject(AppService);
    private actions$ = inject(Actions);
    private store = inject(Store);
    private modalService = inject(ModalService);
    private playbookOpenAiService = inject(PlaybookOpenAiService);
    private convertor = inject(Convertor);
    private basicContentService = inject(BasicContentService);
    private globalTablesService = inject(GlobalTablesService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private dialog = inject(MatDialog);

    loadUserLocationResources$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadUserLocations),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            exhaustMap(([action, vs]) => {
                return from(this.appService.userLocationService.getUserLocationResources(vs.database, action.locations, action.fq)).pipe(
                    map(res => {
                        if (res.status) {
                            return UserRecordActions.setUsers({ records: res.data });
                        }
                    }),
                    catchError(_err => {
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadPlaybookObjects$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadPlaybookObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            exhaustMap(([_, vs]) => {
                const databases = getDatabasesOptions(
                    <SingleViewStateRecord>vs,
                    this.nomWebServicesJson.PSTServer,
                    this.nomWebServicesJson.PSTGroup,
                    this.globalTablesService.AllGroups,
                    this.globalTablesService.AllGroupsArray
                );
                return forkJoin(
                    databases.map(database => {
                        return from(this.playbookService.loadPlaybookObjects(vs, null, database));
                    })
                ).pipe(
                    exhaustMap(response => {
                        let topics = new Map<string, StorytellingTopicRecord>();
                        let assignments = new Map<string, StorytellingAssignmentRecord>();
                        let stories = new Map<string, StorytellingStoryRecord>();
                        let discoverItems = new Map<string, StorytellingRecord>();
                        let listGuids = new Set<string>();
                        response.forEach(res => {
                            if (res.status) {
                                topics = new Map([...topics, ...res.data.topics]);
                                assignments = new Map([...assignments, ...res.data.assignments]);
                                stories = new Map([...stories, ...res.data.stories]);
                                discoverItems = new Map([...discoverItems, ...res.data.discoverItems]);
                                listGuids = new Set([...listGuids, ...res.data.listGuids]);
                            }
                        });
                        return [
                            TopicRecordActions.setTopics({ records: topics }),
                            AssignmentRecordActions.setAssignments({ records: assignments }),
                            StoriesRecordActions.setStories({ records: stories }),
                            DiscoverRecordActions.setDiscoverItems({ records: discoverItems }),
                            ListActions.setListGuids({ listGuids: listGuids }),
                            ListActions.startPolling(),
                        ];
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    loadRundownObjects$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadRundownObjects),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            exhaustMap(([_, vs]) => {
                const databases = getDatabasesOptions(
                    <SingleViewStateRecord>vs,
                    this.nomWebServicesJson.PSTServer,
                    this.nomWebServicesJson.HelloResults.HomeProgram,
                    this.globalTablesService.AllGroups,
                    this.globalTablesService.AllGroupsArray
                );
                return forkJoin(
                    databases.map(database => {
                        return from(this.playbookService.loadRundowns(vs, database));
                    })
                ).pipe(
                    exhaustMap(response => {
                        const rundowns = new Map<string, StorytellingCollectionRecord>();
                        response.forEach(res => {
                            if (res.status) {
                                // @TODO Process rundowns
                                return [...rundowns, []];
                            }
                        });
                        return [];
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    /*searchPlaybook$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.searchPlaybook),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            exhaustMap(([_, vs]) => {
                return from(this.playbookService.loadTopicsV2(vs)).pipe(
                    exhaustMap(res => {
                        if (res.status) {
                            return [
                                TopicRecordActions.setTopics({ records: res.data.topics }),
                                AssignmentRecordActions.setAssignments({ records: res.data.assignments }),
                                StoriesRecordActions.setStories({ records: res.data.stories }),
                                DiscoverRecordActions.setDiscoverItems({ records: res.data.discoverItems }),
                                ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                ListActions.startPolling(),
                            ];
                        }
                        return throwError(() => {
                            'Failed to load topics';
                        });
                    }),
                    catchError(err => {
                        console.error(err);
                        // return of(StoryActions.stopStoryLoading());
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });*/

    loadSingleItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadSingleItem),
            concatLatestFrom(() => this.store.select(selectSystemSingleViewStateItem)),
            exhaustMap(([action, item]) => {
                return from(this.playbookService.loadPlaybookObjects(item.viewState, action.guid)).pipe(
                    exhaustMap(res => {
                        if (res.status) {
                            const isTopic = !!res.data.topics.get(action.guid);
                            const isAssignment = !!res.data.assignments.get(action.guid);
                            const isStory = !!res.data.stories.get(action.guid);

                            if (isTopic) {
                                const title = res.data.topics.get(action.guid).Title;
                                return [
                                    NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.TOPIC, title: title }),
                                    TopicRecordActions.setTopics({ records: res.data.topics }),
                                    StoriesRecordActions.setStories({ records: res.data.stories }),
                                    ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                ];
                            }
                            if (isAssignment) {
                                const title = res.data.assignments.get(action.guid).Title;
                                return [
                                    NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.ASSIGNMENT, title: title }),
                                    AssignmentRecordActions.setAssignments({ records: res.data.assignments }),
                                    ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                ];
                            }
                            if (isStory) {
                                const title = res.data.stories.get(action.guid).Title;
                                return [
                                    NavigationActions.setSingleNavigationItemData({ itemtype: ObjectTypes.SCRIPT, title: title }),
                                    TopicRecordActions.setTopics({ records: res.data.topics }),
                                    StoriesRecordActions.setStories({ records: res.data.stories }),
                                    ListActions.setListGuids({ listGuids: res.data.listGuids }),
                                ];
                            }
                        } else {
                            return throwError(() => {
                                'Failed to load item';
                            });
                        }
                    }),
                    catchError(err => {
                        console.error(err);
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    loadNewItems$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadNewItems),
            concatLatestFrom(() => this.store.select(selectListNewGuids)),
            exhaustMap(([_, guids]) => {
                return from(this.playbookSearchService.getNewItems(guids)).pipe(
                    exhaustMap(res => {
                        if (res.status) {
                            return [
                                TopicRecordActions.updateTopics({ records: [...res.data.topics.values()] }),
                                AssignmentRecordActions.updateAssignments({ records: [...res.data.assignments.values()] }),
                                StoriesRecordActions.updateStories({ records: [...res.data.stories.values()] }),
                            ];
                        }
                        return throwError(() => {
                            'Failed to load new items';
                        });
                    }),
                    catchError(err => {
                        console.error(err);
                        // not sure if we need this
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    loadRundowns$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.loadRundowns),
            concatLatestFrom(() => this.store.select(selectCurrentViewState)),
            exhaustMap(([action, currentVs]) => {
                const vs = action.vs || currentVs;
                if (vs instanceof SingleViewStateRecord) {
                    const databases = getDatabasesOptions(
                        vs,
                        this.nomWebServicesJson.PSTServer,
                        this.nomWebServicesJson.HelloResults.HomeProgram,
                        this.globalTablesService.AllGroups,
                        this.globalTablesService.AllGroupsArray
                    );

                    return forkJoin(
                        databases.map(database => {
                            return from(this.rundownService.loadRundownsV2(vs, database));
                        })
                    ).pipe(
                        exhaustMap(response => {
                            let rundowns = new Map<string, StorytellingRundownRecord>();
                            let stories = new Map<string, StorytellingStoryRecord>();
                            let listGuids = new Set<string>();
                            response.forEach(res => {
                                if (res.status) {
                                    rundowns = new Map([...rundowns, ...res.data.rundowns]);
                                    stories = new Map([...stories, ...res.data.stories]);
                                    listGuids = new Set([...listGuids, ...res.data.listGuids]);
                                }
                            });
                            return [
                                RundownRecordActions.setRundowns({ records: rundowns }),
                                StoriesRecordActions.setStories({ records: stories }),
                                ListActions.setListGuids({ listGuids: listGuids }),
                                ListActions.startPolling(),
                            ];
                        }),
                        catchError(err => {
                            console.error(err);
                            return of(FullscreenActions.noOpAction());
                        })
                    );
                }
            })
        );
    });

    moveRecordToGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.moveRecordToGroup),
            concatLatestFrom(() => [
                this.store.select(selectCurrentViewState),
                this.store.select(selectTopicRecordsMap),
                this.store.select(selectCurrentGroupBy),
                this.store.select(selectCurrentSortBy),
                this.store.select(selectFieldDefsDict),
                this.store.select(selectGroupedSortedTopics),
                this.store.select(selectI18N),
            ]),
            switchMap(([action, viewState, topicsMap, groupBy, sortBy, fieldDefs, sortedTopics, i18n]) => {
                const isTimeField = groupBy.endsWith(TIME_FIELD_SUFFIX);

                const fieldDef = isTimeField ? fieldDefs[groupBy] || fieldDefs[groupBy.replace(TIME_FIELD_SUFFIX, '')] : fieldDefs[groupBy];

                const sourceRecord = topicsMap.get(action.sourceGuid);
                const sourceGroup = action.sourceGroup;
                const targetGroup = action.targetGroup;

                let targetRecord = null;
                if (action.targetGuid) {
                    targetRecord = topicsMap.get(action.targetGuid);
                }

                const params = {
                    sortBy: sortBy,
                    groupBy: groupBy,
                    sourceRecord: sourceRecord,
                    sourceGroup: sourceGroup,
                    targetRecord: targetRecord,
                    targetGroup: targetGroup,
                    viewStateGuid: viewState.guid,
                    sortedTopics: sortedTopics,
                    fieldDef: fieldDef,
                    isTimeField,
                };

                if (['checklistedit', 'staffidlist', 'checklist'].indexOf(fieldDef.Validation.toLowerCase()) > -1) {
                    const value = sourceRecord[fieldDef.ID];

                    if (value && value.length > 1) {
                        const modalContent = i18n.PSTWebConfirmChangeTopicGroup?.replace('{fieldName}', fieldDef.DefaultCaption);
                        return this.modalService.openModal('Update item', modalContent, 'Yes', 'No', 'NoButton').result.pipe(
                            filter(res => res === ModalResponseCodes.OK),
                            map(() => params)
                        );
                    }
                }
                return of(params);
            }),
            switchMap(params => {
                if (params.sortBy === PSTFieldNames.PSTTopicOrder) {
                    let actions: Action[] = [];

                    let lastValidOrder: number = 1000;
                    let currentOrder: number = 0;
                    let targetRecord: StorytellingTopicRecord = null;
                    let targetRecordOrder: number = 1000;

                    let needsReorder: boolean = false;

                    if (!params.targetRecord && params.sortedTopics.entries[params.targetGroup].length > 0) {
                        params.targetRecord = params.sortedTopics.entries[params.targetGroup][0];
                    }

                    for (let currentIdx = 0; currentIdx < params.sortedTopics.entries[params.targetGroup].length; currentIdx++) {
                        const currentRecord: StorytellingTopicRecord = params.sortedTopics.entries[params.targetGroup][currentIdx];
                        const currentViewStateOrder: ViewStateOrder = currentRecord[PSTFieldNames.PSTTopicOrder] || new ViewStateOrder();
                        const currentRecordOrder: number = currentViewStateOrder.get(params.viewStateGuid);

                        needsReorder = false;
                        if (!currentRecordOrder || currentRecordOrder === 0 || currentRecordOrder !== lastValidOrder) {
                            needsReorder = true;
                            currentOrder = lastValidOrder;
                        }

                        if (needsReorder) {
                            currentViewStateOrder.set(params.viewStateGuid, currentOrder);
                            const data: Array<{ key: string; value: any }> = [];
                            data.push({
                                key: PSTFieldNames.PSTTopicOrder,
                                value: currentViewStateOrder,
                            });
                            data.push({
                                key: PSTFieldNames.Type,
                                value: currentRecord.Type,
                            });
                            actions = [...actions, RecordHttpActions.saveRecordChanges({ record: currentRecord, fieldsToUpdate: data })];
                            lastValidOrder = currentOrder;
                        }

                        lastValidOrder += 1000;

                        if (currentRecord.Guid === params.targetRecord.Guid) {
                            targetRecord = currentRecord;
                            targetRecordOrder = currentOrder;
                        }
                    }

                    if (targetRecord) {
                        currentOrder = targetRecordOrder - 500;
                        const currentViewStateOrder: ViewStateOrder = params.sourceRecord[PSTFieldNames.PSTTopicOrder] || new ViewStateOrder();
                        currentViewStateOrder.set(params.viewStateGuid, currentOrder);

                        const data = getTopicFieldsToUpdate(params.sourceRecord, params.fieldDef, params.targetGroup);
                        data.push({
                            key: PSTFieldNames.PSTTopicOrder,
                            value: currentViewStateOrder,
                        });
                        actions = [...actions, RecordHttpActions.saveRecordChanges({ record: params.sourceRecord, fieldsToUpdate: data })];
                    }

                    return actions;
                } else {
                    const fieldsToUpdate = getTopicFieldsToUpdate(params.sourceRecord, params.fieldDef, params.targetGroup, params.isTimeField);
                    return of(RecordHttpActions.saveRecordChanges({ record: params.sourceRecord, fieldsToUpdate: fieldsToUpdate }));
                }
            })
        );
    });

    // TODO: Might need a better name
    saveChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.saveRecordChanges),
            concatLatestFrom(() => [this.store.select(selectFullscreenNewRecord)]),
            switchMap(([action, newRecord]) => {
                const record = { ...action.record };
                const fieldsToUpdate = action.fieldsToUpdate;
                if (record?.isNewRecord) {
                    for (const field of fieldsToUpdate) {
                        record[field.key] = field.value;
                    }

                    switch (newRecord.Type) {
                        case ObjectTypes.SCRIPT: {
                            return of(FullscreenActions.updateCurrentNewRecord({ record: record as StorytellingStoryRecord }));
                        }
                        case ObjectTypes.TOPIC: {
                            // TODO: check this
                            if (record.Type === ObjectTypes.SCRIPT) {
                                //Update existing record
                                const storyIdx = (newRecord as StorytellingTopicRecord)?.stories?.findIndex(story => story.Guid === record.Guid);
                                if (storyIdx !== -1) {
                                    (record as StorytellingStoryRecord).FullStorySlug = record.Title;
                                    const newStories = [...(newRecord as StorytellingTopicRecord).stories];
                                    newStories[storyIdx] = { ...record };
                                    const updatedRecord = { ...newRecord, stories: newStories };
                                    return of(FullscreenActions.updateCurrentNewRecord({ record: updatedRecord }));
                                }
                            } else if (record.Type === ObjectTypes.ASSIGNMENT) {
                                //Update existing record
                                const assignmentIdx = (newRecord as StorytellingTopicRecord)?.children?.findIndex(assignment => assignment.Guid === record.Guid);
                                if (assignmentIdx !== -1) {
                                    const newAssignments = [...(newRecord as StorytellingTopicRecord).children];
                                    newAssignments[assignmentIdx] = record;
                                    const updatedRecord = { ...newRecord, children: newAssignments };
                                    return of(FullscreenActions.updateCurrentNewRecord({ record: updatedRecord }));
                                }
                            }
                            return of(FullscreenActions.updateCurrentNewRecord({ record }));
                        }
                    }
                } else {
                    //Update existing record
                    const data: { [id: string]: any } = {};

                    const titleField = fieldsToUpdate.find(field => field.key === PSTFieldNames.Title);
                    if (titleField && typeof titleField.value === 'string' && titleField.value.toString().trim() === '') {
                        return;
                    }

                    let textUpdated = false;
                    let hasTextCommands = false;
                    for (const field of fieldsToUpdate) {
                        textUpdated = textUpdated || field.key === PSTFieldNames.PSTRichText || field.key === PSTFieldNames.PSTDraftText;
                        const enpsField = this.convertor.serializeField(field.value, field.key);

                        if (!(typeof field.value === 'string' || typeof field.value === 'number' || typeof field.value === 'boolean') && enpsField.usingRawValue) {
                            Util.saveWarningError(`EnpsControl.saveChanges() not implemented for type ${typeof field.value}`);
                            return;
                        }

                        if (enpsField.key === PSTFieldNames.TextCommands) {
                            const textCommandKeys = Object.keys(enpsField.value);
                            const textCommandValues = Object.values(enpsField.value);
                            if (textCommandKeys.length === textCommandValues.length && textCommandKeys.length > 0) {
                                data['TextCommandKeys'] = textCommandKeys;
                                data['TextCommandValues'] = textCommandValues;
                                hasTextCommands = true;
                            }
                        } else {
                            data[enpsField.key] = enpsField.value;
                        }

                        // TODO: A little hack
                        if (this.globalTablesService.isDateTimeFieldType(field.key) && record[field.key] && field?.value?.timeZone) {
                            record[field.key].timeZone = field?.value?.timeZone;
                        }
                    }

                    // TODO: A little hack for the backend and the blackline removing when we don't have text commands
                    if (!hasTextCommands && textUpdated) {
                        data['TextCommandKeys'] = [];
                        data['TextCommandValues'] = [];
                    }

                    // Make sure to pass record type
                    data.Type = record.Type;

                    const database = record.Database || this.nomWebServicesJson.PSTServer;

                    return from(this.basicContentService.updateBasicContentV2(database, record.Path.fullPath, record.Guid, false, data, false, record)).pipe(
                        exhaustMap(res => {
                            if (!res.status) {
                                throw new Error('Error updating record');
                            }
                            // TODO: ALL TYPES OF RECORDS
                            switch (record.Type) {
                                case ObjectTypes.TOPIC:
                                    return [TopicRecordActions.updateTopics({ records: [record] }), FullscreenActions.setLoadingProgressBar({ showLoader: false })];
                                case ObjectTypes.ASSIGNMENT:
                                    return [AssignmentRecordActions.updateAssignments({ records: [record] }), FullscreenActions.setLoadingProgressBar({ showLoader: false })];
                                case ObjectTypes.SCRIPT:
                                    record.hasUnsavedChanges = false;
                                    return [StoriesRecordActions.updateStories({ records: [record] }), FullscreenActions.setLoadingProgressBar({ showLoader: false })];
                            }
                        })
                    );
                }
            }),
            catchError(err => {
                console.error(err);
                // return of(StoryActions.stopStoryLoading());
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    subscribeForRealtimeUpdates$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    TopicRecordActions.setTopics,
                    AssignmentRecordActions.setAssignments,
                    StoriesRecordActions.setStories,
                    RundownRecordActions.setRundowns,
                    PublicationRecordActions.setPublicationChannels,
                    PublicationRecordActions.setPublications,
                    PublicationRecordActions.setPublicationList
                ),
                mergeMap(action => {
                    return forkJoin([
                        Array.from(action.records).map(rec => {
                            // TODO this needs to be an effect and we need to store subscribed records in the store
                            from(DataStore.subscribeForRecordUpdatesV2(rec[1]));
                            return rec;
                        }),
                    ]);
                })
            );
        },
        { dispatch: false }
    );

    subscribeForRealtimeUpdatesUpdate$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(
                    TopicRecordActions.updateTopics,
                    AssignmentRecordActions.updateAssignments,
                    StoriesRecordActions.updateStories,
                    RundownRecordActions.updateRundowns,
                    PublicationRecordActions.updatePublications,
                    PublicationRecordActions.updatePublicationList,
                    PublicationRecordActions.updatePublicationChannels
                ),
                mergeMap(action => {
                    return forkJoin([
                        action.records.map((rec: StorytellingRecord) => {
                            // TODO this needs to be an effect and we need to store subscribed records in the store
                            from(DataStore.subscribeForRecordUpdatesV2(rec));
                            return rec;
                        }),
                    ]);
                })
            );
        },
        { dispatch: false }
    );

    duplicateTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.duplicateTopic),
            concatLatestFrom(() => [this.store.select(selectCurrentViewStateType), this.store.select(selectCurrentRecord)]),
            switchMap(([action, currentViewStateType, currentRecord]) => {
                return from(this.playbookService.duplicateItem(action.linkToParent, action.oldRecord, action.isNew)).pipe(
                    map(rec => rec.data as StorytellingTopicRecord),
                    switchMap(newRecord => {
                        let actions: Action[] = [
                            TopicRecordActions.updateTopics({ records: [newRecord] }),
                            AssignmentRecordActions.updateAssignments({ records: newRecord.children || [] }),
                            StoriesRecordActions.updateStories({ records: newRecord.stories || [] }),
                        ];
                        if (currentRecord) {
                            actions = [...actions, FullscreenActions.openTopic({ recordGuid: newRecord?.Guid, recordType: newRecord?.Type })];
                        } else {
                            actions = [...actions, OpenFullscreenModalActions.openTopicFullscreenDialog({ recordGuid: newRecord?.Guid, recordType: newRecord?.Type })];
                        }
                        if (currentViewStateType === ViewStateTypes.TOPIC) {
                            actions = [...actions, ListActions.addListGuids({ listGuids: [newRecord.Guid] })];
                        }
                        return actions;
                    })
                );
            })
        );
    });

    duplicateAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.duplicateAssignment),
            concatLatestFrom(action => {
                return [this.store.select(selectAssignments), this.store.select(selectTopicRecordByGuid(action.record.ParentGuid)), this.store.select(selectCurrentViewStateType)];
            }),
            switchMap(([action, assignments, parentRecord, currentViewStateType]) => {
                return from(this.playbookService.duplicateAssignment(action.record, action.linkToParent)).pipe(
                    map(rec => rec.data),
                    switchMap(newRecord => {
                        assignments.push(newRecord);
                        let actions: Action[] = [TopicRecordActions.updateTopics({ records: [parentRecord] }), AssignmentRecordActions.updateAssignments({ records: assignments || [] })];
                        if (currentViewStateType === ViewStateTypes.ASSIGNMENT) {
                            actions = [...actions, ListActions.addListGuids({ listGuids: [newRecord.Guid] })];
                        }
                        return actions;
                    })
                );
            })
        );
    });

    deleteRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.deleteRecord),
            concatLatestFrom(() => {
                return [this.store.select(selectFullscreenNewRecord), this.store.select(selectTopicRecordsMap), this.store.select(selectI18N)];
            }),
            switchMap(([action, newRecord, topics, languageSettings]) => {
                const { record } = action;
                if (newRecord && newRecord?.isNewRecord) {
                    return of(FullscreenActions.removeNewRecordItems({ record: record }));
                } else {
                    const modalMessageText = () => {
                        switch (record?.Type) {
                            case ObjectTypes.TOPIC:
                                return `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteTopicPart2}`;
                            case ObjectTypes.ASSIGNMENT:
                                return `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteAssignmentPart2}`;
                            case ObjectTypes.SCRIPT:
                                return `${languageSettings.PSTWebConfirmDeletePart1}${record?.Title}${languageSettings.PSTWebConfirmDeleteStoryPart2}${languageSettings.PSTWebConfirmDeleteStoryPart3}`;
                            default:
                                return '';
                        }
                    };
                    const modalContent = modalMessageText();
                    return this.modalService.openModal(languageSettings.PSTWebConfirmDeleteLabel, modalContent, languageSettings.PSTWebYesOption, languageSettings.PSTWebNoOption).result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        switchMap(() => {
                            return from(this.basicContentService.deleteBasicContent(record?.Database, record?.Path.fullPath, record?.Guid, record?.Type, record)).pipe(
                                switchMap(res => {
                                    if (!res.status) {
                                        throw new Error('Error deleting record');
                                    }

                                    const deletedRecord = res.data?.deletedRecord;
                                    const updatedRecords = res.data?.updatedRecords;

                                    if (deletedRecord?.Type === ObjectTypes.TOPIC) {
                                        return [
                                            TopicRecordActions.deleteTopics({ records: [deletedRecord] }),
                                            OpenFullscreenModalActions.closeFullscreenModal(),
                                            ListActions.removeListGuids({ listGuids: [deletedRecord.Guid] }),
                                        ];
                                    }
                                    if (deletedRecord?.Type === ObjectTypes.ASSIGNMENT) {
                                        return [AssignmentRecordActions.deleteAssignments({ records: [deletedRecord] })];
                                    }
                                    if (deletedRecord?.Type === ObjectTypes.SCRIPT) {
                                        const actions: Action[] = [StoriesRecordActions.deleteStories({ records: [deletedRecord] })];

                                        // TODO: I think this piece of code should be executed always, no matter the deleted record type
                                        if (updatedRecords?.topics?.size > 0) {
                                            actions.push(TopicRecordActions.updateTopics({ records: [...updatedRecords.topics.values()] }));
                                        }
                                        if (updatedRecords?.assignments?.size > 0) {
                                            actions.push(AssignmentRecordActions.updateAssignments({ records: [...updatedRecords.assignments.values()] }));
                                        }
                                        if (updatedRecords?.stories?.size > 0) {
                                            actions.push(StoriesRecordActions.updateStories({ records: [...updatedRecords.stories.values()] }));
                                        }
                                        if (updatedRecords?.rundowns?.size > 0) {
                                            actions.push(RundownRecordActions.updateRundowns({ records: [...updatedRecords.rundowns.values()] }));
                                        }
                                        const currentTopic = topics.get((deletedRecord as StorytellingStoryRecord).PSTStoryTopicGUID);
                                        if (currentTopic) {
                                            actions.push(FullscreenActions.openTopic({ recordGuid: currentTopic?.Guid, recordType: ObjectTypes.TOPIC }));
                                        }
                                        return actions;
                                    }
                                    //TODO Add delete for Rundowns
                                    return [ListActions.removeListGuids({ listGuids: [deletedRecord.Guid] })];
                                })
                            );
                        })
                    );
                }
            })
        );
    });

    openNewTemplateDialog$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RecordHttpActions.openNewTemplateDialog),
                switchMap(action => {
                    this.dialog.open(CreateNewTemplateComponent, {
                        panelClass: 'st-create-new-template',
                        width: '22rem',
                        height: '22rem',
                        hasBackdrop: true,
                        data: action.record,
                    });
                    return of();
                })
            );
        },
        { dispatch: false }
    );

    shouldProceedAttachment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.shouldProceedAttachment),
            switchMap(async action => {
                const files: AttachmentFiles[] = [];
                for (const attachment of action.recordAttachments) {
                    if (attachment) {
                        const shouldProceed = await this.appService.shouldProceedAttachment(attachment.name, attachment.nativeFile.size);
                        if (shouldProceed) {
                            files.push({ file: attachment.nativeFile, finished: false, name: attachment.name });
                        }
                    }
                }
                return RecordHttpActions.createAttachment({ files: files, record: action.record });
            })
        );
    });

    fixMovedStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoriesRecordActions.fixMovedStory),
            switchMap(action => {
                const record = action.record;
                const storyRecords = this.store.selectSignal(selectStoryRecordsMap)();
                const currentOpenedRecord = this.store.selectSignal(selectCurrentRecord)();
                let newStory: StorytellingStoryRecord = null;
                for (const story of record.children) {
                    if (story?.OriginalGUID) {
                        if (storyRecords.has(story?.OriginalGUID)) {
                            if (story?.OriginalGUID === currentOpenedRecord?.Guid) {
                                newStory = story;
                            }
                        }
                    }
                }

                if (newStory) {
                    return [
                        FullscreenActions.openStory({ recordGuid: newStory.Guid, recordType: ObjectTypes.SCRIPT }),
                        StoriesRecordActions.deleteStories({ records: [{ Guid: newStory.OriginalGUID }] }),
                    ];
                }
                return of(ApplicationActions.noOpAction());
            })
            // LEFT FOR REFERENCE
            // concatMap(story => {
            //     return from(this.basicContentService.getRecord<StorytellingStoryRecord>(story.Database, story.Path.fullPath, story.Guid)).pipe(
            //         switchMap(response => {
            //             if (response.status) {
            //                 return [StoriesRecordActions.updateStories({ records: [response.data] }), StoriesRecordActions.deleteStories({ records: [{ Guid: response.data.OriginalGUID }] })];
            //             }
            //         })
            //     );
            // })
        );
    });

    createAttachment$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RecordHttpActions.createAttachment),
                switchMap(async action => {
                    const files = action.files;
                    const currentRecord = action.record;
                    let actions: Action[] = [];
                    if (files.length > 0) {
                        let baseFiles = [];
                        baseFiles = await this.appService.readUrl(files);

                        for (const file of baseFiles) {
                            const dialogResult = await this.appService.dialogService.open(AttachmentRenameComponent, { hasBackdrop: true, data: { fileName: file.Filename } }).closed.toPromise();
                            if (dialogResult) {
                                file.name = dialogResult;
                                file.Filename = dialogResult;
                                file.Title = dialogResult;
                                file.ExternalFiles = currentRecord.ExternalFiles;
                                const res = await this.appService.attachmentService.createAttachment(
                                    currentRecord.Database,
                                    currentRecord.Path.fullPath,
                                    currentRecord.Guid,
                                    false,
                                    file,
                                    currentRecord
                                );

                                if (res && res.status) {
                                    if (currentRecord.Type === ObjectTypes.TOPIC) {
                                        actions = [...actions, TopicRecordActions.updateTopics({ records: [currentRecord] })];
                                    } else {
                                        actions = [...actions, AssignmentRecordActions.updateAssignments({ records: [currentRecord] })];
                                    }
                                }
                            }
                        }
                        return actions;
                    }
                })
            );
        },
        { dispatch: false }
    );

    nativeAndroidUpload$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.nativeAndroidUpload),
            switchMap(action => {
                const currentRecord = action.record;
                if (action.nativeAndroidEvent && action.nativeAndroidEvent.detail && action.nativeAndroidEvent.detail.file) {
                    const fileSize = Math.ceil(action.nativeAndroidEvent.detail.file.length / 4) * 3;
                    let actions: Action[] = [];
                    return from(this.appService.shouldProceedAttachment(action.nativeAndroidEvent.detail.fileName, fileSize)).pipe(
                        switchMap(shouldProceed => {
                            if (shouldProceed) {
                                const file: any = {
                                    Type: ObjectTypes.EXTERNAL,
                                    FileBytes: action.nativeAndroidEvent.detail.file,
                                    Title: action.nativeAndroidEvent.detail.fileName,
                                    Filename: action.nativeAndroidEvent.detail.fileName,
                                };
                                file.ExternalFiles = currentRecord.ExternalFiles;
                                return from(
                                    this.appService.attachmentService.createAttachment(currentRecord.Database, currentRecord.Path.fullPath, currentRecord.Guid, false, file, currentRecord)
                                ).pipe(
                                    switchMap(res => {
                                        if (res && res.status) {
                                            if (currentRecord.Type === ObjectTypes.TOPIC) {
                                                actions = [...actions, TopicRecordActions.updateTopics({ records: [currentRecord] })];
                                            } else {
                                                actions = [...actions, AssignmentRecordActions.updateAssignments({ records: [currentRecord] })];
                                            }
                                        }
                                        return actions;
                                    })
                                );
                            }
                        })
                    );
                }
            })
        );
    });

    removeAttachment$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(RecordHttpActions.removeAttachment),
                switchMap(action => {
                    const currentRecord = action.record;
                    currentRecord.ExternalFiles.remove(action.attachment.guid);
                    return from(this.appService.attachmentService.deleteAttachment(currentRecord, action.attachment));
                })
            );
        },
        { dispatch: false }
    );

    onDeleteRecordTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(RecordHttpActions.deleteRecordTemplate),
            switchMap(action => {
                const { guid, recordType } = action;
                let record: StorytellingRecord = null;
                switch (recordType) {
                    case ObjectTypes.TOPIC:
                        record = this.store.selectSignal(selectTopicTemplatesMap)()?.get(guid);
                        break;
                    case ObjectTypes.SCRIPT:
                        record = this.store.selectSignal(selectStoryTemplatesMap)()?.get(guid);
                        break;
                }
                if (record) {
                    const languageSettings = this.store.selectSignal(selectI18N)();
                    const modalContent = languageSettings.PSTWebConfirmDeletePart1 + record?.Title + languageSettings.PSTWebConfirmDeleteTemplatePart2;
                    return this.modalService.openModal(languageSettings.PSTWebConfirmDeleteLabel, modalContent, languageSettings.PSTWebYesOption, languageSettings.PSTWebNoOption).result.pipe(
                        take(1),
                        filter(res => res === ModalResponseCodes.OK),
                        exhaustMap(() => {
                            return from(this.appService.basicContentService.deleteBasicContent(record.Database, record.Path.fullPath, record.Guid, record.Type)).pipe(
                                filter(delRes => delRes.status),
                                exhaustMap(() => {
                                    // TODO: Yanko could not be happy with this, BUT I prefer it that way (NED) <3
                                    switch (recordType) {
                                        case ObjectTypes.TOPIC:
                                            return of(PlaybookApiActions.loadTopicTemplates());
                                        case ObjectTypes.SCRIPT:
                                            return of(PlaybookApiActions.loadStoryTemplates());
                                    }
                                })
                            );
                        })
                    );
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    onSetStoryHasUnsavedChanges$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoriesRecordActions.setStoryHasUnsavedChanges),
            map(action => {
                const storiesMap = this.store.selectSignal(selectStoryRecordsMap)();
                const story = storiesMap.get(action.storyGuid);
                const updatedStory = { ...story, hasUnsavedChanges: action.hasUnsavedChanges };

                return StoriesRecordActions.updateStories({ records: [updatedStory] });
            })
        );
    });
}
